import React, { useState, useEffect } from 'react';
import { getHrisPelatihan, getHrisPelatihanById, createHrisPelatihan, updateHrisPelatihan, deleteHrisPelatihan, getKaryawan, uploadHrisPelatihanFile } from '../../api';
import { Plus, Edit2, Trash2, Award, Search, CheckCircle, Download, Calendar, User, DollarSign, Eye, X, ExternalLink, FileText, Upload } from 'lucide-react';

const JENIS_PELATIHAN = ['Internal', 'Eksternal', 'Sertifikasi'];
const STATUS_PELATIHAN = ['Direncanakan', 'Berlangsung', 'Selesai', 'Batal'];

const statusConfig = {
  'Direncanakan': { bg: '#fef3c7', color: '#92400e', dot: '#fb6340' },
  'Berlangsung': { bg: '#eef9fd', color: '#0a66c2', dot: '#11cdef' },
  'Selesai': { bg: '#d1fae5', color: '#065f46', dot: '#2dce89' },
  'Batal': { bg: '#fee2e2', color: '#991b1b', dot: '#f5365c' },
};

export default function HrisPelatihan() {
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    karyawan_id: '', nama_pelatihan: '', penyelenggara: '', tgl_mulai: '', tgl_selesai: '',
    jenis: 'Internal', biaya: 0, sertifikat: false, no_sertifikat: '', masa_berlaku_sertifikat: '', lampiran: '', keterangan: ''
  });
  const [fileData, setFileData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const fetchData = () => {
    setLoading(true);
    Promise.all([getHrisPelatihan(), getKaryawan()])
      .then(([dRes, kRes]) => { setData(dRes.data); setKaryawan(kRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setFileData(null);
    setForm({ karyawan_id: '', nama_pelatihan: '', penyelenggara: '', tgl_mulai: '', tgl_selesai: '',
      jenis: 'Internal', biaya: 0, sertifikat: false, no_sertifikat: '', masa_berlaku_sertifikat: '', lampiran: '', keterangan: '' });
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await getHrisPelatihanById(id);
      const d = res.data;
      setEditing(d);
      setFileData(null);
      setForm({
        karyawan_id: d.karyawan_id, nama_pelatihan: d.nama_pelatihan, penyelenggara: d.penyelenggara,
        tgl_mulai: d.tgl_mulai, tgl_selesai: d.tgl_selesai || '', jenis: d.jenis, biaya: d.biaya,
        sertifikat: d.sertifikat, no_sertifikat: d.no_sertifikat,
        masa_berlaku_sertifikat: d.masa_berlaku_sertifikat || '', status: d.status, keterangan: d.keterangan
      });
      setShowModal(true);
    } catch (e) { alert('Gagal memuat data'); }
  };

  const handleSubmit = async () => {
    if (!form.karyawan_id || !form.nama_pelatihan || !form.tgl_mulai) { alert('Lengkapi field wajib'); return; }
    try {
      let lampiran = form.lampiran || '';
      if (fileData) {
        setUploading(true);
        const uploadRes = await uploadHrisPelatihanFile(fileData, editing ? editing.id : '0');
        lampiran = uploadRes.data.filename;
        setUploading(false);
      }
      const payload = { ...form, lampiran };
      if (editing) {
        await updateHrisPelatihan(editing.id, { ...payload, status: form.status || 'Selesai' });
      } else {
        await createHrisPelatihan(payload);
      }
      setShowModal(false);
      fetchData();
    } catch (e) { alert('Gagal menyimpan'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data pelatihan ini?')) return;
    try { await deleteHrisPelatihan(id); fetchData(); } catch (e) { alert('Gagal menghapus'); }
  };

  const fmtRupiah = (v) => `Rp ${Number(v).toLocaleString('id-ID')}`;

  const totalData = data.length;
  const totalSelesai = data.filter(d => d.status === 'Selesai').length;
  const totalBerlangsung = data.filter(d => d.status === 'Berlangsung').length;
  const totalRencana = data.filter(d => d.status === 'Direncanakan').length;

  const filtered = data.filter(d => {
    if (search && !d.nama?.toLowerCase().includes(search.toLowerCase()) && !d.nama_pelatihan?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus && d.status !== filterStatus) return false;
    return true;
  });

  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  return (
    <div className="page-container">
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <Award size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan-500)' }} />
          Data Pelatihan & Sertifikasi
        </h2>
      </div>

      {/* Stat Cards */}
      {data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--info)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total Pelatihan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gray-600)', lineHeight: 1.2 }}>{totalData}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #2dce89' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Selesai</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46', lineHeight: 1.2 }}>{totalSelesai}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #11cdef' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Berlangsung</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0a66c2', lineHeight: 1.2 }}>{totalBerlangsung}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #fb6340' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Direncanakan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#92400e', lineHeight: 1.2 }}>{totalRencana}</div>
          </div>
        </div>
      )}

      {/* Toolbar: Tambah | Search | Status */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 16px', background: '#fff', borderRadius: 12,
        boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
        flexWrap: 'wrap',
      }}>
        <button className="btn btn-primary" onClick={openCreate} style={{ whiteSpace: 'nowrap' }}>
          <Plus size={16} /> Tambah Pelatihan
        </button>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <div className="search-box" style={{ width: '100%', margin: 0 }}>
            <Search size={16} />
            <input placeholder="Cari berdasarkan nama..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Status:</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="form-select"
            style={{ width: 'auto', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}>
            <option value="">Semua</option>
            {STATUS_PELATIHAN.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
            <strong style={{ color: 'var(--gray-600)' }}>{filtered.length}</strong>/{totalData}
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="page-loading">Tidak ada data pelatihan</div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
          {filtered.map(d => {
            const sc = statusConfig[d.status] || statusConfig['Direncanakan'];
            return (
              <div key={d.id} className="region-card" style={{ borderTop: `3px solid ${sc.dot}` }}>
                <div className="region-card-header" style={{
                  background: '#fff', borderBottom: '1px solid var(--gray-100)',
                  padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'linear-gradient(135deg, var(--cyan-500), var(--cyan-700))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      <Award size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '0.95rem', margin: 0, color: 'var(--gray-600)' }}>{d.nama}</h3>
                        <span style={{
                          fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4,
                          background: '#e6fafc', color: 'var(--cyan-700)', fontWeight: 500,
                        }}>{d.jenis}</span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                          background: sc.bg, color: sc.color,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot }} />
                          {d.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 2 }}>
                        {d.nama_pelatihan}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="region-card-body" style={{ padding: '12px 16px' }}>
                  <div className="region-detail"><User size={14} className="text-muted" /><strong>Penyelenggara:</strong> {d.penyelenggara || '-'}</div>
                  <div className="region-detail"><Calendar size={14} className="text-muted" /><strong>Tanggal:</strong> {d.tgl_mulai}{d.tgl_selesai ? ` - ${d.tgl_selesai}` : ''}</div>
                  <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Biaya:</strong> {fmtRupiah(d.biaya)}</div>
                  {d.sertifikat && (
                    <div className="region-detail">
                      <CheckCircle size={14} style={{ color: '#2dce89' }} />
                      <strong>Sertifikat:</strong> {d.no_sertifikat || 'Ada'}
                      {d.masa_berlaku_sertifikat && <span style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}> (s/d {d.masa_berlaku_sertifikat})</span>}
                    </div>
                  )}
                  {d.keterangan && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8 }}>
                      <span style={{ color: 'var(--gray-500)', fontSize: '0.8rem', fontStyle: 'italic' }}>{d.keterangan}</span>
                    </div>
                  )}
                </div>
                <div className="action-btns" style={{ padding: '10px 16px', borderTop: '1px solid var(--gray-100)', justifyContent: 'flex-end' }}>
                  {d.lampiran && (
                    <button className="btn-icon" title="Lihat lampiran" onClick={() => setPreviewItem(d)} style={{ color: 'var(--cyan-500)' }}>
                      <Eye size={14} />
                    </button>
                  )}
                  <button className="btn-icon" title="Edit" onClick={() => openEdit(d.id)}><Edit2 size={14} /></button>
                  <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Award size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{editing ? 'Edit Pelatihan' : 'Tambah Pelatihan'}</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)',
                border: '1px solid var(--cyan-100)', overflow: 'hidden',
              }}>
                {/* Section Header 1 */}
                <div style={{
                  padding: '12px 16px', borderBottom: '1px solid var(--cyan-100)',
                  background: 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: 'var(--cyan-700)',
                  textAlign: 'center',
                }}>
                  <Award size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Informasi Pelatihan
                </div>

                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Karyawan <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: parseInt(e.target.value)})} required>
                      <option value="">Pilih Karyawan</option>
                      {karyawan.map(k => <option key={k.id} value={k.id}>{k.nama}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Jenis <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.jenis} onChange={e => setForm({...form, jenis: e.target.value})}>
                      {JENIS_PELATIHAN.map(j => <option key={j} value={j}>{j}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label>Nama Pelatihan <span style={{color:'var(--danger)'}}>*</span></label>
                    <input type="text" value={form.nama_pelatihan} onChange={e => setForm({...form, nama_pelatihan: e.target.value})} required placeholder="Contoh: Pelatihan Mekanik Lanjutan" />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Penyelenggara</label>
                    <input type="text" value={form.penyelenggara} onChange={e => setForm({...form, penyelenggara: e.target.value})} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Biaya</label>
                    <input type="number" value={form.biaya} onChange={e => setForm({...form, biaya: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Mulai <span style={{color:'var(--danger)'}}>*</span></label>
                    <input type="date" value={form.tgl_mulai} onChange={e => setForm({...form, tgl_mulai: e.target.value})} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Selesai</label>
                    <input type="date" value={form.tgl_selesai} onChange={e => setForm({...form, tgl_selesai: e.target.value})} />
                  </div>
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                      <input type="checkbox" checked={form.sertifikat} onChange={e => setForm({...form, sertifikat: e.target.checked})} />
                      Ada Sertifikat / Lisensi
                    </label>
                  </div>
                  {form.sertifikat && (
                    <>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>No. Sertifikat</label>
                        <input type="text" value={form.no_sertifikat} onChange={e => setForm({...form, no_sertifikat: e.target.value})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Masa Berlaku</label>
                        <input type="date" value={form.masa_berlaku_sertifikat} onChange={e => setForm({...form, masa_berlaku_sertifikat: e.target.value})} />
                      </div>
                    </>
                  )}
                  {editing && (
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Status</label>
                      <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                        {STATUS_PELATIHAN.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* File Upload + Keterangan */}
                <div style={{
                  borderTop: '1px dashed var(--cyan-200)', background: 'rgba(255,255,255,0.4)',
                  padding: '14px 16px',
                }}>
                  <div className="file-upload-wrapper" style={{ margin: 0 }}>
                    <label style={{
                      fontSize: '0.75rem', fontWeight: 600, marginBottom: 8,
                      color: 'var(--gray-600)', display: 'block',
                    }}>
                      <Upload size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      File Lampiran (PDF/Word/Foto)
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <input type="file" id="pelatihan-file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={e => setFileData(e.target.files[0])}
                        style={{ display: 'none' }} />
                      <button type="button" className="btn btn-ghost"
                        onClick={() => document.getElementById('pelatihan-file').click()}
                        style={{ fontSize: '0.8rem', padding: '6px 14px' }}>
                        <Upload size={14} /> {fileData ? fileData.name : 'Pilih File'}
                      </button>
                      {form.lampiran && !fileData && (
                        <button type="button" className="btn btn-sm btn-ghost"
                          onClick={() => setPreviewItem({ file_kontrak: form.lampiran, nama: form.nama_pelatihan, no_kontrak: form.no_sertifikat })}
                          style={{ fontSize: '0.78rem', color: 'var(--cyan-600)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Eye size={13} /> Lihat File
                        </button>
                      )}
                      {uploading && <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Mengupload...</span>}
                    </div>
                  </div>
                  <div className="form-group" style={{ margin: '10px 0 0' }}>
                    <label>Keterangan</label>
                    <textarea value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} rows={2} style={{ fontSize: '0.82rem' }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={uploading}>{uploading ? 'Mengupload...' : editing ? 'Simpan' : 'Tambah'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Lampiran Modal */}
      {previewItem && (previewItem.file_kontrak || previewItem.lampiran) && (() => {
        const filename = previewItem.file_kontrak || previewItem.lampiran;
        const url = `/uploads/pelatihan/${filename}`;
        const ext = filename.split('.').pop().toLowerCase();
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
        const isPdf = ext === 'pdf';

        return (
          <div className="modal-overlay" onClick={() => setPreviewItem(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}
              style={{ maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px', borderBottom: '1px solid var(--gray-100)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: 'linear-gradient(135deg, var(--cyan-500), var(--cyan-700))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FileText size={16} color="#fff" />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0, color: 'var(--gray-600)' }}>
                      Lampiran — {previewItem.nama || 'File'}
                    </h3>
                    <small style={{ color: 'var(--gray-400)' }}>{filename}</small>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <a href={url}
                     target="_blank" rel="noopener noreferrer"
                     className="btn btn-ghost" title="Buka di tab baru"
                     style={{ padding: '8px 12px', fontSize: '0.78rem' }}>
                    <ExternalLink size={14} /> Buka
                  </a>
                  <button className="btn-icon" onClick={() => setPreviewItem(null)} title="Tutup">
                    <X size={18} />
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 0, background: '#f8f9fe' }}>
                {isImage ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <img src={url} alt="Lampiran"
                      style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                      onError={e => {
                        e.target.style.display = 'none';
                        const fb = document.getElementById(`pl-err-${filename.replace(/[^a-z0-9]/gi,'')}`);
                        if (fb) fb.style.display = 'block';
                      }} />
                    <div id={`pl-err-${filename.replace(/[^a-z0-9]/gi,'')}`} style={{ display: 'none', padding: '40px 20px', color: 'var(--gray-500)' }}>
                      <FileText size={48} style={{ color: 'var(--gray-300)', marginBottom: 12 }} />
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>Gagal memuat gambar</p>
                    </div>
                  </div>
                ) : isPdf ? (
                  <iframe src={url} title="Preview PDF" style={{ width: '100%', height: '75vh', border: 'none' }} sandbox="allow-scripts allow-same-origin" />
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <FileText size={64} style={{ color: 'var(--gray-300)', marginBottom: 16 }} />
                    <h4 style={{ color: 'var(--gray-500)', margin: '0 0 8px', fontSize: '1rem' }}>File {ext.toUpperCase()}</h4>
                    <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: 20 }}>Preview tidak tersedia untuk file {ext.toUpperCase()}.</p>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Download size={16} /> Buka File {ext.toUpperCase()}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
